import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete, TextField, CircularProgress, Box, Typography,
} from '@mui/material';
import { AddCircleOutline as CreateIcon } from '@mui/icons-material';
import debounce from 'lodash/debounce';
import wineService from '../../services/wineService';
import type { WineSearchResult } from '../../services/wineService';
import { useAuth } from '../../context/AuthContext';

export type { WineSearchResult };

// Sentinel option appended at the bottom of the dropdown
const CREATE_OPTION_ID = '__create_wine__';
type CreateOption = { _id: typeof CREATE_OPTION_ID; name: string; __isCreate__: true };
type DropdownOption = WineSearchResult | CreateOption;

const isCreateOption = (o: DropdownOption): o is CreateOption =>
  (o as CreateOption).__isCreate__ === true;

interface Props {
  value: WineSearchResult | null;
  onChange: (wine: WineSearchResult | null) => void;
  onCreateNew?: (query: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  label?: string;
  /**
   * When provided, search hits the venue-scoped lookup endpoint
   * (`/admin/venue/:venueId/wine-lookup/search`) which is open to VENUE_USER.
   * Omit only from admin-context callers operating outside any venue.
   */
  venueId?: string;
}

const COLOR_DOT: Record<string, string> = {
  RED: '#922B21',
  WHITE: '#c9a227',
  PINK_ROSE: '#c2185b',
};

const WineSearchBox: React.FC<Props> = ({
  value,
  onChange,
  onCreateNew,
  error,
  helperText,
  disabled,
  label = 'Wine',
  venueId,
}) => {
  const { isVenueUser } = useAuth();
  // CreateWineModal lets the user create Producer / Region / Grape records
  // inline as a side-effect of creating the Wine. VENUE_USER must not touch
  // those global identity tables, so the sentinel is hidden for them even if
  // a parent component passed an onCreateNew callback. The proper "request a
  // provisional wine" flow for venue users is tracked separately (U1G).
  const canCreate = !!onCreateNew && !isVenueUser();

  const [options, setOptions] = useState<WineSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Hold the current venueId in a ref so the long-lived debounced function
  // always reads the latest value without being rebuilt on every render.
  const venueIdRef = useRef(venueId);
  useEffect(() => { venueIdRef.current = venueId; }, [venueId]);

  const debouncedSearch = useRef(
    debounce(async (q: string) => {
      if (q.trim().length < 2) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setSearchFailed(false);
      try {
        const results = await wineService.search(q, 10, venueIdRef.current);
        setOptions(results);
      } catch {
        setOptions([]);
        setSearchFailed(true);
      } finally {
        setLoading(false);
      }
    }, 300),
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInput: string) => {
    setInputValue(newInput);
    setSearchFailed(false);
    if (newInput.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      debouncedSearch.cancel();
      return;
    }
    setLoading(true);
    debouncedSearch(newInput);
  }, [debouncedSearch]);

  const createOption: CreateOption = {
    _id: CREATE_OPTION_ID,
    name: '+ Create new wine',
    __isCreate__: true,
  };

  // Append the create sentinel only when the caller wants it AND the current
  // user is allowed to use it (admins).
  const dropdownOptions: DropdownOption[] = canCreate
    ? [...options, createOption]
    : options;

  const noOptionsText = (() => {
    if (inputValue.trim().length < 2) return 'Type at least 2 characters to search';
    if (loading) return 'Searching…';
    if (searchFailed) return 'Search failed — check your connection';
    return 'No wines found';
  })();

  return (
    <Autocomplete<DropdownOption>
      value={value}
      inputValue={inputValue}
      options={dropdownOptions}
      loading={loading}
      disabled={disabled}
      filterOptions={x => x}
      getOptionLabel={option => isCreateOption(option) ? '' : (option?.name ?? '')}
      isOptionEqualToValue={(option, val) => {
        if (isCreateOption(option) || isCreateOption(val as DropdownOption)) return false;
        return (option as WineSearchResult)._id === (val as WineSearchResult)._id;
      }}
      onInputChange={handleInputChange}
      onChange={(_e, newValue) => {
        if (!newValue) {
          onChange(null);
          setOptions([]);
          return;
        }
        if (isCreateOption(newValue)) {
          // Don't update selected value — open create modal instead.
          // Belt-and-suspenders: only fire if creation is actually allowed
          // for this user. The sentinel won't render in the dropdown
          // otherwise, but this guards against any stale references.
          if (canCreate) onCreateNew?.(inputValue);
          return;
        }
        onChange(newValue as WineSearchResult);
        setOptions([]);
      }}
      noOptionsText={noOptionsText}
      renderOption={(props, option) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };

        if (isCreateOption(option)) {
          return (
            <li key={key} {...rest}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, width: '100%', color: 'primary.main' }}>
                <CreateIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  Create new wine…
                </Typography>
              </Box>
            </li>
          );
        }

        const wine = option as WineSearchResult;
        const color = wine.wineDisplay?.color;
        const producer = wine.wineDisplay?.producerName;
        const region = wine.wineDisplay?.regionName;
        const subtitle = [producer, region].filter(Boolean).join(' · ');
        return (
          <li key={key} {...rest}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, width: '100%' }}>
              <Box
                sx={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: color ? (COLOR_DOT[color] ?? '#bbb') : '#bbb',
                  opacity: color ? 1 : 0.35,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {wine.name ?? '—'}
                </Typography>
                {subtitle && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required
          error={error || searchFailed}
          helperText={
            helperText !== undefined
              ? helperText
              : !value && inputValue.trim().length < 2
                ? 'Type at least 2 characters to search'
                : undefined
          }
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default WineSearchBox;
